import { CreateEmployeeContractBody } from "@entity/data/dto/employee/contracts/employee/create-employee-contract.body";
import { DisabilityTypeEnumMapper } from "@entity/data/mappers/employee/disability-type-enum.mapper";
import { ProfessionalGroupEnumMapper } from "@entity/data/mappers/employee/professional-group-enum.mapper";
import { SubsidyReasonEnumMapper } from "@entity/data/mappers/employee/subsidy-reason-enum.mapper";
import { TerminationReasonEnumMapper } from "@entity/data/mappers/employee/termination-reason-enum.mapper";
import { CreateEmployeeContract } from "@entity/domain/models/employee/contracts/employee/create-employee-contract.model";
import { EmployeeContractFormValuesValidated } from "@entity/presentation/component/employee-form/contract/employee/form/employee-contract-form";
import { ContractTypeEnumMapper } from "@project/data/mappers/expenses/contract-type-enum.mapper";
import { ContributionGroupTypeEnumMapper } from "@project/data/mappers/expenses/contribution-group-type-enum.mapper";
import { inject, injectable } from "inversify";

@injectable()
export class CreateEmployeeContractMapper {
    constructor(
        @inject(ContributionGroupTypeEnumMapper)
        private readonly contributionGroupTypeEnumMapper: ContributionGroupTypeEnumMapper,
        @inject(ContractTypeEnumMapper)
        private readonly contractTypeEnumMapper: ContractTypeEnumMapper,
        @inject(DisabilityTypeEnumMapper)
        private readonly disabilityTypeEnumMapper: DisabilityTypeEnumMapper,
        @inject(ProfessionalGroupEnumMapper)
        private readonly professionalGroupEnumMapper: ProfessionalGroupEnumMapper,
        @inject(TerminationReasonEnumMapper)
        private readonly terminationReasonEnumMapper: TerminationReasonEnumMapper,
        @inject(SubsidyReasonEnumMapper)
        private readonly subsidyReasonEnumMapper: SubsidyReasonEnumMapper,
    ) {}

    mapToCreateDto(
        createEmployeeContract: CreateEmployeeContract,
    ): CreateEmployeeContractBody {
        const contractType = this.contractTypeEnumMapper.mapToDto(
            createEmployeeContract.type,
        );

        const professionalGroup = this.professionalGroupEnumMapper.mapToDto(
            createEmployeeContract.professionalGroup,
        );

        const contributionGroup = this.contributionGroupTypeEnumMapper.mapToDto(
            createEmployeeContract.contributionGroup,
        );

        let disabilityType = null;
        if (createEmployeeContract.disabilityType) {
            disabilityType = this.disabilityTypeEnumMapper.mapToDto(
                createEmployeeContract.disabilityType,
            );
        }

        let terminationReason = null;
        if (createEmployeeContract.terminationReason) {
            terminationReason = this.terminationReasonEnumMapper.mapToDto(
                createEmployeeContract.terminationReason,
            );
        }

        let subsidyReason = null;
        if (createEmployeeContract.subsidyReason) {
            subsidyReason = this.subsidyReasonEnumMapper.mapToDto(
                createEmployeeContract.subsidyReason,
            );
        }

        const createEmployeeContractDto: CreateEmployeeContractBody = {
            type: contractType,
            start_date: createEmployeeContract.startDate.toISODate() ?? "",
            has_disability: createEmployeeContract.hasDisability,
            professional_group: professionalGroup,
            contribution_group: contributionGroup,
            weekly_workday_hours:
                createEmployeeContract.weeklyWorkdayHours.toString(),
            net_salary: createEmployeeContract.netSalary.toString(),
            IRPF: createEmployeeContract.irpf.toString(),
            social_security_contribution:
                createEmployeeContract.socialSecurityContribution.toString(),
            monthly_cost: createEmployeeContract.monthlyCost.toString(),
            social_security_contribution_company:
                createEmployeeContract.socialSecurityContributionCompany.toString(),
            total_cost: createEmployeeContract.totalCost.toString(),
            deductible_amount_disability:
                createEmployeeContract.deductibleAmountDisability.toString(),
            employee: createEmployeeContract.employeeId,
            termination_reason: terminationReason,
            disability_type: disabilityType ?? undefined,
            other_type: createEmployeeContract.otherType ?? undefined,
            is_subsidized: createEmployeeContract.isSubsidized ?? false,
            subsidy_deductible_amount:
                createEmployeeContract.subsidyDeductibleAmount?.toString() ??
                undefined,
            subsidy_reason: subsidyReason ?? undefined,
            other_subsidy_reason:
                createEmployeeContract.otherSubsidyReason ?? undefined,
            subsidy_scope: createEmployeeContract.subsidyScope ?? undefined,
            end_date: createEmployeeContract.endDate?.toISODate() ?? null,
        };

        return createEmployeeContractDto;
    }

    mapFromFormValues(
        employeeId: number,
        employeeContractForm: EmployeeContractFormValuesValidated,
    ): CreateEmployeeContract {
        if (!employeeId) throw Error("Missing employeeId");

        const createEmployeeContract = new CreateEmployeeContract(
            employeeContractForm.type,
            employeeContractForm.startDate,
            employeeContractForm.hasDisability,
            employeeContractForm.professionalGroup,
            employeeContractForm.contributionGroup,
            employeeContractForm.weeklyWorkdayHours,
            employeeContractForm.netSalary,
            employeeContractForm.irpf,
            employeeContractForm.socialSecurityContribution,
            employeeContractForm.monthlyCost,
            employeeContractForm.socialSecurityContributionCompany,
            employeeContractForm.totalCost,
            employeeContractForm.deductibleAmountDisability,
            employeeId,
            employeeContractForm.disabilityType ?? undefined,
            employeeContractForm.terminationReason ?? undefined,
            employeeContractForm.otherType ?? undefined,
            employeeContractForm.isSubsidized,
            employeeContractForm.subsidyDeductibleAmount ?? undefined,
            employeeContractForm.subsidyReason ?? undefined,
            employeeContractForm.otherSubsidyReason ?? undefined,
            employeeContractForm.subsidyScope ?? undefined,
            employeeContractForm.endDate ?? undefined,
        );

        return createEmployeeContract;
    }
}
